import {
  CheckoutPaymentIntent,
  ItemCategory,
  Order,
  OrderRequest,
  PaypalWalletContextShippingPreference,
} from "@paypal/paypal-server-sdk"
import { CheckoutContactData } from "src/checkout/state/checkoutContactStore"
import { StorefrontCart } from "src/types/storefront"

export function mapWcPrice(wcPrice: string | number | null) {
  return wcPrice ? (+wcPrice / 100).toFixed(2) : "0.00"
}

export function mapToPayPalOrder(cart: StorefrontCart) {
  const total = +cart.totals.total_price
  const shippingTotal = +(cart.totals.total_shipping || 0) + +(cart.totals.total_shipping_tax || 0)
  const itemsTotal = total - shippingTotal

  // https://developer.paypal.com/docs/api/orders/v2/#orders_create
  const payload: OrderRequest = {
    intent: CheckoutPaymentIntent.Authorize,
    purchaseUnits: [
      {
        // This is the default value. It is just set for explicitness.
        referenceId: "default",

        amount: {
          currencyCode: cart.totals.currency_code,
          value: mapWcPrice(total),

          breakdown: {
            itemTotal: {
              currencyCode: cart.totals.currency_code,
              value: mapWcPrice(itemsTotal),
            },
            shipping: {
              currencyCode: cart.totals.currency_code,
              value: mapWcPrice(shippingTotal),
            },
          },
        },

        items: cart.items.map((item) => ({
          name: item.name,
          quantity: item.quantity.toString(),
          sku: item.sku,
          category: ItemCategory.PhysicalGoods,
          unitAmount: {
            currencyCode: item.totals.currency_code,
            value: mapWcPrice((+item.totals.line_total + +item.totals.line_total_tax) / item.quantity),
          },
        })),
      },
    ],
    paymentSource: {
      paypal: {
        experienceContext: {
          shippingPreference: PaypalWalletContextShippingPreference.GetFromFile,
        },
      },
    },
  }

  return payload
}

export function mapPayPalAmountToPlainApiFormat(amount: OrderRequest["purchaseUnits"][number]["amount"]) {
  return {
    currency_code: amount.currencyCode,
    value: amount.value,

    breakdown: {
      item_total: {
        currency_code: amount.breakdown?.itemTotal?.currencyCode,
        value: amount.breakdown?.itemTotal?.value,
      },
      shipping: {
        currency_code: amount.breakdown?.shipping?.currencyCode,
        value: amount.breakdown?.shipping?.value,
      },
    },
  }
}

export function mapPayPalOrderToCheckoutContactState(order: Order) {
  const address = order.purchaseUnits?.[0]?.shipping?.address
  const firstName = order.payer?.name?.givenName || ""
  const lastName = order.payer?.name?.surname || ""
  const email = order.payer?.emailAddress || ""
  const phone = order.payer?.phone?.phoneNumber?.nationalNumber || ""
  const countryCode = address?.countryCode || ""
  const regionCode = address?.adminArea1 || ""
  const postalCode = address?.postalCode || ""
  const city = address?.adminArea2 || ""
  const street = address?.addressLine1 || ""
  const note = ""

  return {
    useBillingAddressForShipping: true,
    shippingAddress: {
      firstName: { value: firstName },
      lastName: { value: lastName },
      companyName: { value: "" },
      countryCode: { value: countryCode, requiresConfirmation: false, confirmed: false },
      regionCode: { value: regionCode, requiresConfirmation: false, confirmed: false },
      postalCode: { value: postalCode, requiresConfirmation: false, confirmed: false },
      city: { value: city, requiresConfirmation: false, confirmed: false },
      street: { value: street, requiresConfirmation: false, confirmed: false },
      note: note,
    },
    billingAddress: {
      email: { value: email, focused: false },
      phone: { value: phone, focused: false },
      phoneNormalized: phone,
      firstName: { value: firstName, focused: false },
      lastName: { value: lastName, focused: false },
      companyName: { value: "", focused: false },
      countryCode: { value: countryCode, requiresConfirmation: false, confirmed: false },
      regionCode: { value: regionCode, requiresConfirmation: false, confirmed: false },
      postalCode: { value: postalCode, requiresConfirmation: false, confirmed: false },
      city: { value: city, requiresConfirmation: false, confirmed: false },
      street: { value: street, requiresConfirmation: false, confirmed: false },
      note: note,
    },
  } satisfies Partial<CheckoutContactData>
}
